import { useState, type FormEvent } from 'react';
import { Button, Input, Label, Modal } from '@immo/ui-kit';
import { UpdateAdminApiKeyBodySchema, type GetAdminApiKeyResponse } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { apiClient } from '@/lib/auth/api-client';
import { SearchIcon } from '@/components/layout/icons';
import { PermissionCatalogue } from '@/components/permission-catalogue';
import {
  groupAdminGrantablePermissions,
  useModuleLabel,
  usePermissionLabel,
  usePermissionLiteral,
} from '@/lib/permissions/catalogue';

const PERMISSION_GROUPS = groupAdminGrantablePermissions();

const RATE_LIMIT_MIN = 1;
const RATE_LIMIT_MAX = 60;

function sameSet(left: ReadonlyArray<string>, right: ReadonlyArray<string>): boolean {
  if (left.length !== right.length) return false;

  const other = new Set(right);

  return left.every((entry) => other.has(entry));
}

export type EditApiKeyDialogProps = {
  readonly apiKey: GetAdminApiKeyResponse;
  readonly memberLabel: string;
  readonly onClose: () => void;
  readonly onSaved: () => void;
};

/**
 * „API-Key bearbeiten" (IMM-493.2) — rewrites an issued key's granted
 * permissions and rate limit in place, without revoking it. One `PATCH` carries
 * both fields, so the change is one request and one audit row.
 *
 * A modal rather than edit-in-place on the detail rows: the catalogue is a tall
 * scrolling list with its own search and per-group select-all, which sits badly
 * inside the page's two-column `<dl>`. This matches both shipped dialogs on the
 * surface (issue, revoke) and reuses the `dirty` close guard.
 */
export function EditApiKeyDialog({ apiKey, memberLabel, onClose, onSaved }: EditApiKeyDialogProps) {
  const t = useTranslations('pages.admin.api-keys.edit');
  const tIssue = useTranslations('pages.admin.api-keys.issue');
  const permissionLabel = usePermissionLabel();
  const permissionIdentifier = usePermissionLiteral();
  const moduleLabel = useModuleLabel();

  const [rateLimit, setRateLimit] = useState(String(apiKey.rateLimit));
  const [permissions, setPermissions] = useState<ReadonlyArray<string>>(apiKey.grantedPermissions);
  const [permissionQuery, setPermissionQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsedRate = Number(rateLimit.trim());
  const rateValid =
    rateLimit.trim() !== '' &&
    Number.isInteger(parsedRate) &&
    parsedRate >= RATE_LIMIT_MIN &&
    parsedRate <= RATE_LIMIT_MAX;

  // Client-side mirrors of the contract's own bounds — a convenience so the
  // refusal needs no round-trip, never a replacement for the server's checks.
  const submitDisabled = submitting || permissions.length === 0 || !rateValid;
  const dirty = !sameSet(permissions, apiKey.grantedPermissions) || parsedRate !== apiKey.rateLimit;

  const togglePermission = (permission: string) =>
    setPermissions((prev) =>
      prev.includes(permission) ? prev.filter((entry) => entry !== permission) : [...prev, permission],
    );

  const setGroupSelection = (groupPermissions: ReadonlyArray<string>, selected: boolean) =>
    setPermissions((prev) => {
      if (selected) {
        const next = new Set(prev);

        for (const permission of groupPermissions) next.add(permission);

        return [...next];
      }

      return prev.filter((permission) => !groupPermissions.includes(permission));
    });

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    const parsed = UpdateAdminApiKeyBodySchema.safeParse({
      rateLimit: parsedRate,
      grantedPermissions: permissions,
    });

    if (!parsed.success) {
      setError(t('errors.generic'));

      return;
    }

    setSubmitting(true);

    try {
      const result = await apiClient.adminApiKeys.update({ pathParams: { id: apiKey.id }, body: parsed.data });

      if (result.ok) {
        onSaved();

        return;
      }

      const body = result.body as { error?: { code?: string; message?: string } } | undefined;
      const code = body?.error?.code;

      if (code === 'identity.api-key.cannot-edit-revoked') {
        // Reachable when the key was revoked in another tab after this page loaded.
        setError(t('errors.revoked'));
      } else if (code === 'identity.api-key.permission-not-held') {
        // Names no permission (IMM-569.3) — the wire message would echo the
        // bound member's own authorisation state back over the API response.
        setError(tIssue('errors.permission-not-held', { member: memberLabel }));
      } else if (result.status === 403) {
        setError(t('errors.forbidden'));
      } else {
        setError(t('errors.generic'));
      }
    } catch {
      setError(t('errors.generic'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => {
        if (!submitting) onClose();
      }}
      dirty={dirty}
      size="md"
      title={t('title')}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose} disabled={submitting}>
            {t('cancel-cta')}
          </Button>
          <Button type="submit" form="edit-api-key-form" disabled={submitDisabled}>
            {submitting ? t('submit-loading') : t('submit-cta')}
          </Button>
        </>
      }
    >
      <form id="edit-api-key-form" className="space-y-5" onSubmit={submit}>
        <div className="space-y-2">
          <Label htmlFor="edit-api-key-rate-limit">{t('rate-limit-label')}</Label>
          <div className="flex items-center gap-2">
            <Input
              id="edit-api-key-rate-limit"
              type="number"
              min={RATE_LIMIT_MIN}
              max={RATE_LIMIT_MAX}
              className="w-28"
              value={rateLimit}
              disabled={submitting}
              aria-invalid={!rateValid}
              onChange={(e) => setRateLimit(e.target.value)}
            />
            <span className="text-sm text-text-tertiary">{tIssue('rate-limit-suffix')}</span>
          </div>
          {!rateValid && <p className="text-sm text-danger-600">{tIssue('errors.rate-limit-range')}</p>}
        </div>

        <div className="space-y-2">
          <Label>{t('permissions-label')}</Label>
          <p className="text-sm text-text-tertiary">{tIssue('permissions-helper')}</p>
          <div className="relative">
            <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" />
            <Input
              className="pl-8"
              value={permissionQuery}
              placeholder={tIssue('permission-search-placeholder')}
              onChange={(e) => setPermissionQuery(e.target.value)}
              disabled={submitting}
              aria-label={tIssue('permission-search-placeholder')}
            />
          </div>
          <PermissionCatalogue
            groups={PERMISSION_GROUPS}
            query={permissionQuery}
            selected={permissions}
            disabled={submitting}
            label={permissionLabel}
            literal={permissionIdentifier}
            moduleLabel={moduleLabel}
            scopeLabel={(scope) => tIssue(`scope.${scope}`)}
            selectAllLabel={tIssue('select-all')}
            deselectAllLabel={tIssue('deselect-all')}
            emptyLabel={tIssue('permission-empty')}
            onToggle={togglePermission}
            onSetGroup={setGroupSelection}
          />
          {permissions.length === 0 && (
            <p className="text-sm text-danger-600">{tIssue('errors.permissions-required')}</p>
          )}
        </div>

        {error !== null && <p className="text-base text-danger-600">{error}</p>}
      </form>
    </Modal>
  );
}
